import React, { useMemo, useState } from 'react';
import {
	Row,
	Col,
	Button,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from 'react-query';
import { EditOutlined } from '@ant-design/icons';
import { formatCurrency } from '@shared/utils/currency';
import HIIHirePeriod from '@shared/hireInvoice/HIIHirePeriod';
import type { GetVoyageHireItemsResponse } from '@api/features/voyages/getVoyageHireItems';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import Card from '@client/components/Card/Card';
import PeriodScheduleTable from '@client/screens/fixtures/tc/PeriodScheduleTable';
import Table from '@client/components/Table/Table';
import { getVoyageHireItems } from '@client/lib/api';
import StateTag from '@client/components/StateTag';
import { useVoyage } from '../components/VoyageProvider/VoyageProvider';
import EditHireItemDrawer from './components/EditHireItemDrawer';

// `row.itemType` is a human-readable label ('Hire'), so the underlying item type is
// the only reliable way to single out hire periods.
const isHirePeriodRow = (row: GetVoyageHireItemsResponse[number]) => (
	(row.item as any)?.__itemType === HIIHirePeriod.itemType
);

const HireTab = () => {
	const [editingItem, setEditingItem] = useState<GetVoyageHireItemsResponse[number] | null>(null);
	const queryClient = useQueryClient();

	const {
		voyageId,
		voyageDetails,
		fixtureDetails,
		fixtureCurrency,
		fixtureType,
		refreshVoyageDetails,
		refreshDetails,
	} = useVoyage();

	const {
		data: hireItems,
		isLoading: hireItemsLoading,
	} = useQuery(
		['hireItems', voyageId],
		() => getVoyageHireItems(voyageId),
		{
			staleTime: 30_000,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
		},
	);

	const hirePeriodRows = useMemo(
		() => (hireItems ?? []).filter(isHirePeriodRow),
		[hireItems],
	);

	const firstHirePeriodItemId = hirePeriodRows[0]?.item.id ?? null;
	const latestHirePeriodItemId = hirePeriodRows[hirePeriodRows.length - 1]?.item.id ?? null;

	const columns = useMemo<ColumnsType<GetVoyageHireItemsResponse[number]>>(() => ([
		{
			title: 'Invoice #',
			dataIndex: 'invoiceNumber',
		},
		{
			title: 'Invoice title',
			dataIndex: 'invoiceTitle',
		},
		{
			title: 'Type',
			dataIndex: 'itemType',
		},
		{
			title: 'Description',
			dataIndex: 'hireDescription',
			render: (description: string) => description || '-',
		},
		{
			title: 'Quantity',
			dataIndex: 'quantity',
			align: 'right',
		},
		{
			title: 'Price',
			dataIndex: 'price',
			align: 'right',
			render: (rate: number) => formatCurrency(rate, fixtureCurrency),
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			align: 'right',
			render: (rate: number) => formatCurrency(rate, fixtureCurrency),
		},
		{
			title: 'State',
			dataIndex: 'state',
			align: 'right',
			render: (state) => (
				<StateTag
					state={state}
				/>
			),
		},
		{
			title: '',
			align: 'right',
			render: (row: GetVoyageHireItemsResponse[number]) => (
				<Button
					type="link"
					icon={<EditOutlined />}
					onClick={() => setEditingItem(row)}
				/>
			),
		},
	]), [fixtureCurrency]);

	if (voyageDetails == null) {
		return null;
	}

	const tcFixtureDetails =
		fixtureDetails as GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps>;

	return (
		<Row gutter={[16, 16]}>
			<Col span={24}>
				<Card>
					<PeriodScheduleTable
						fixture={tcFixtureDetails}
						periodSchedule={voyageDetails.periodSchedule}
						fixtureCurrency={fixtureCurrency}
						refreshFixture={refreshVoyageDetails}
						afterSave={refreshDetails}
						tcTerms={voyageDetails.tcTerms}
					/>
				</Card>
			</Col>
			<Col span={24}>
				<Card
					slim
					size="small"
					title="Hire items"
				>
					<Table<GetVoyageHireItemsResponse[number]>
						rowKey={(row) => row.item.id}
						size="small"
						loading={hireItemsLoading}
						pagination={false}
						columns={columns}
						dataSource={hireItems ?? []}
						emptyText="No hire items on invoices yet"
					/>
				</Card>
			</Col>
			<EditHireItemDrawer
				editingItem={editingItem}
				latestHirePeriodItemId={latestHirePeriodItemId}
				firstHirePeriodItemId={firstHirePeriodItemId}
				onClose={() => setEditingItem(null)}
				voyageId={voyageId}
				fixtureCurrency={fixtureCurrency}
				fixtureType={fixtureType}
				onSuccess={() => {
					queryClient.invalidateQueries(['hireItems', voyageId]);
					refreshVoyageDetails();
				}}
			/>
		</Row>
	);
};

export default HireTab;